import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import {
  authenticatedGet,
  authenticatedPut,
  authenticatedDelete,
} from "../utils/apiUtils";

const AssetDetails = () => {
  const { id } = useParams();
  const [asset, setAsset] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Handle session expiration for encryption keys
  const handleSessionExpired = (errorData) => {
    Swal.fire({
      icon: "warning",
      title: "Session Expired",
      text: errorData.message || "Your session has expired. Please authenticate again to access encrypted items.",
      showCancelButton: true,
      confirmButtonText: "Authenticate Now",
      cancelButtonText: "Go Back",
    }).then((result) => {
      if (result.isConfirmed) {
        // Redirect to assets list where they can authenticate
        navigate("/?requiresAuth=true");
      } else {
        // Go back to previous page
        navigate(-1);
      }
    });
  };

  // Fetch asset on component mount
  useEffect(() => {
    const fetchAsset = async () => {
      setLoading(true);
      try {
        const response = await authenticatedGet(`/assets/${id}`);

        if (response.ok) {
          const assetData = await response.json();
          // Ensure properties array exists
          if (!assetData.properties) {
            assetData.properties = [];
          }
          setAsset(assetData);
          setLoading(false);
        } else {
          // Check if this is a session expiration error
          const errorData = await response.json().catch(() => null);
          setLoading(false);
          if (errorData?.requiresAuthentication) {
            handleSessionExpired(errorData);
          } else {
            console.error("Error fetching item:", response.status);
            Swal.fire({
              icon: "error",
              title: "Error Loading Asset",
              text: "The asset could not be found. It may have been deleted in another session or you don't have permission to access it.",
              confirmButtonText: "Return to Assets"
            }).then(() => {
              // Redirect to the assets list after the user acknowledges the error
              navigate("/");
            });
          }
        }
      } catch (error) {
        console.error("Error fetching item:", error);
        setLoading(false);
        Swal.fire({
          icon: "error",
          title: "Error Loading Asset",
          text: "There was a problem loading the asset details. Please try again.",
          confirmButtonText: "Return to Assets"
        }).then(() => {
          // Redirect to the assets list after the user acknowledges the error
          navigate("/");
        });
      }
    };

    fetchAsset();
  }, [id]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setAsset({ ...asset, [name]: value });
  };

  const handlePropertyChange = (index, event) => {
    const newProperties = [...asset.properties];
    newProperties[index][event.target.name] = event.target.value;
    setAsset({ ...asset, properties: newProperties });
  };

  const addNewProperty = () => {
    const newProperty = { name: "", value: "" };
    setAsset({
      ...asset,
      properties: [...asset.properties, newProperty],
    });
  };

  const removeProperty = (index) => {
    const newProperties = [...asset.properties];
    newProperties.splice(index, 1);
    setAsset({ ...asset, properties: newProperties });
  };

  const handleSave = async () => {
    const hasEmptyProperties = asset.properties.some(
      (property) => !property.name || !property.value
    );

    if (hasEmptyProperties) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "All details must have a name and value.",
      });
      return;
    }

    try {
      const response = await authenticatedPut(`/assets/${id}`, asset);

      if (response.ok) {
        const updatedAsset = await response.json();
        setAsset(updatedAsset);
        setIsEditing(false);
        Swal.fire({
          icon: "success",
          title: "Item Updated!",
          text: "Your item has been successfully updated.",
        });
      } else {
        // Check if this is a session expiration error
        const errorData = await response.json().catch(() => null);
        
        if (errorData?.requiresAuthentication) {
          handleSessionExpired(errorData);
        } else if (response.status === 404) {
          // Asset no longer exists (has been deleted)
          Swal.fire({
            icon: "error",
            title: "Item No Longer Exists",
            text: "This item appears to have been deleted in another session or client.",
            confirmButtonText: "Return to Assets"
          }).then(() => {
            navigate("/");
          });
        } else {
          throw new Error("Failed to save item");
        }
      }
    } catch (error) {
      console.error("Save error:", error);
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "There was a problem saving the item. The asset may have been deleted in another session or you may not have permission to edit it.",
        confirmButtonText: "OK"
      });
    }
  };

  const handleDeleteAsset = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await authenticatedDelete(`/assets/${id}`);

          if (response.ok) {
            Swal.fire("Deleted!", "The item has been deleted.", "success");
            navigate("/");
          } else {
            // Check if this is a session expiration error
            const errorData = await response.json().catch(() => null);
            if (errorData?.requiresAuthentication) {
              handleSessionExpired(errorData);
            } else {
              throw new Error("Failed to delete item");
            }
          }
        } catch (error) {
          Swal.fire(
            "Error!",
            "There was a problem deleting the item.",
            "error"
          );
        }
      }
    });
  };

  const handleDeleteProperty = (propertyId, index) => {
    // If propertyId exists, it's an existing property that needs API deletion
    // If no propertyId, it's a new property that can be removed locally
    if (propertyId) {
      Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            const response = await authenticatedDelete(
              `/assets/properties/${propertyId}`
            );

            if (response.ok) {
              Swal.fire("Deleted!", "The detail has been deleted.", "success");
              setAsset({
                ...asset,
                properties: asset.properties.filter(
                  (prop) => prop.id !== propertyId
                ),
              });
            } else {
              // Check if this is a session expiration error
              const errorData = await response.json().catch(() => null);
              if (errorData?.requiresAuthentication) {
                handleSessionExpired(errorData);
              } else {
                throw new Error("Failed to delete detail");
              }
            }
          } catch (error) {
            Swal.fire(
              "Error!",
              "There was a problem deleting the detail.",
              "error"
            );
          }
        }
      });
    } else {
      // Remove new property locally (no API call needed)
      removeProperty(index);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading item details...</p>
        </div>
      </div>
    );
  }
  
  if (!asset) {
    // If not loading but no asset, redirect to home
    navigate("/");
    return null;
  }

  return (
    <div className="asset-details-container">
      <div className="asset-details-header">
        {!isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="btn btn-outline-primary edit-toggle-btn"
            title="Edit this item"
          >
            <span className="btn-icon">✏️</span>
            Edit Item
          </button>
        )}
      </div>

      <div className="asset-details-card">
        <div className="asset-main-info">
          <div className="asset-name-section">
            <label className="field-label">Item Name</label>
            {isEditing ? (
              <input
                type="text"
                name="name"
                value={asset.name}
                onChange={handleInputChange}
                className="form-control modern-input asset-name-input"
                placeholder="Enter item name"
              />
            ) : (
              <h1 className="asset-name-display">{asset.name}</h1>
            )}
          </div>

          <div className="asset-description-section">
            <label className="field-label">Description</label>
            {isEditing ? (
              <textarea
                name="description"
                value={asset.description}
                onChange={handleInputChange}
                className="form-control modern-textarea"
                rows="4"
                placeholder="Describe your item"
              />
            ) : (
              <p className="asset-description-display">{asset.description}</p>
            )}
          </div>

          <div className="asset-type-section">
            <label className="field-label">Item Type</label>
            {isEditing ? (
              <select
                name="assetType"
                value={asset.assetType}
                onChange={handleInputChange}
                className="form-control modern-select"
              >
                <option value="Vehicle">Vehicle</option>
                <option value="Appliance">Appliance</option>
                <option value="Real Estate Property">
                  Real Estate Property
                </option>
                <option value="Furniture">Furniture</option>
                <option value="Electronics">Electronics</option>
              </select>
            ) : (
              <span className="asset-type-badge-large">{asset.assetType}</span>
            )}
          </div>
        </div>

        <div className="item-details-section">
          <div className="section-header">
            <h3 className="section-title">Item Details</h3>
            <p className="section-description">
              {asset.properties && asset.properties.length > 0
                ? `${asset.properties.length} detail${
                    asset.properties.length !== 1 ? "s" : ""
                  } recorded`
                : "No details recorded yet"}
            </p>
          </div>

          {asset.properties && asset.properties.length > 0 ? (
            <div className="details-grid">
              {asset.properties.map((property, index) => (
                <div key={index} className="detail-card">
                  {isEditing ? (
                    <div className="detail-edit-form">
                      <div className="detail-edit-header">
                        <span className="detail-number">
                          Detail #{index + 1}
                        </span>
                        <button
                          onClick={() =>
                            handleDeleteProperty(property.id, index)
                          }
                          className="btn btn-outline-danger btn-sm"
                          title="Delete this detail"
                        >
                          <span className="btn-icon">🗑️</span>
                          Delete
                        </button>
                      </div>
                      <div className="detail-inputs">
                        <div className="form-group">
                          <label className="form-label">Detail Name</label>
                          <input
                            type="text"
                            name="name"
                            value={property.name}
                            onChange={(event) =>
                              handlePropertyChange(index, event)
                            }
                            className="form-control modern-input"
                            placeholder="Enter detail name"
                          />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Detail Value</label>
                          <input
                            type="text"
                            name="value"
                            value={property.value}
                            onChange={(event) =>
                              handlePropertyChange(index, event)
                            }
                            className="form-control modern-input"
                            placeholder="Enter detail value"
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="detail-display">
                      <div className="detail-name">{property.name}</div>
                      <div className="detail-value">{property.value}</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="no-details-state">
              <div className="no-details-icon">📝</div>
              <h4>No details available</h4>
              <p>Start editing to add details about this item.</p>
            </div>
          )}

          {isEditing && (
            <div className="add-detail-section">
              <button
                onClick={addNewProperty}
                className="btn btn-outline-primary add-detail-btn"
                type="button"
              >
                <span className="btn-icon">+</span>
                Add New Detail
              </button>
            </div>
          )}
        </div>

        <div className="action-buttons">
          {isEditing ? (
            <div className="edit-actions">
              <button onClick={handleSave} className="btn btn-primary btn-lg">
                <span className="btn-icon">💾</span>
                Save Changes
              </button>
              <button
                onClick={() => setIsEditing(false)}
                className="btn btn-outline-secondary"
              >
                <span className="btn-icon">❌</span>
                Cancel
              </button>
              <button onClick={handleDeleteAsset} className="btn btn-danger">
                <span className="btn-icon">🗑️</span>
                Delete Item
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default AssetDetails;
